/**
 * License Economics – Audit history panel.
 *
 * Loaded for a specific licenseEconomicsId via useGetLicenseEconomicsAudit.
 * Renders create/update/retire events with actor, timestamp, and meaningful
 * snapshot details extracted from the free-form snapshot object.
 */

import { AlertCircle, Clock, Loader2, RefreshCw } from "lucide-react";
import {
  useGetLicenseEconomicsAudit,
  getGetLicenseEconomicsAuditQueryKey,
} from "@workspace/api-client-react";
import type { LicenseEconomicsAuditEvent } from "@workspace/api-client-react";
import { formatTs } from "../scenarios/utils";
import { LICENSE_MODEL_LABELS } from "./types";
import type { LicenseModel } from "@workspace/api-client-react";
import { summarizeTrueUp } from "./types";

// ─── Snapshot detail extraction ───────────────────────────────────────────────

const SNAPSHOT_MONEY_KEYS: string[] = [
  "seatRate",
  "unitRate",
  "fixedMonthlyComponent",
  "minimumMonthlyCommitment",
  "trueUpAmount",
];

const SNAPSHOT_LABEL: Record<string, string> = {
  vendor: "Vendor",
  productWorkload: "Product",
  glAccount: "GL account",
  currentModel: "Current model",
  futureModel: "Future model",
  seats: "Seats",
  activeSeats: "Active seats",
  seatRate: "Seat rate",
  usageUnit: "Usage unit",
  currentMonthlyUsage: "Monthly usage",
  unitRate: "Unit rate",
  fixedMonthlyComponent: "Fixed monthly",
  minimumMonthlyCommitment: "Min. commitment",
  contractStartDate: "Contract start",
  contractEndDate: "Contract end",
  renewalDate: "Renewal date",
  effectiveFiscalYear: "Effective FY",
  effectivePeriod: "Effective period",
  seatTiers: "Seat tiers",
  usageTiers: "Usage tiers",
  trueUpTiming: "True-up timing",
  trueUpFiscalYear: "True-up fiscal year",
  trueUpFiscalPeriod: "True-up period",
  trueUpAmount: "True-up amount",
  sourceReference: "Source reference",
  approvalReference: "Approval reference",
  status: "Status",
};

// Display-friendly value for a snapshot field
function displayValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (
    (key === "currentModel" || key === "futureModel") &&
    typeof value === "string"
  ) {
    return LICENSE_MODEL_LABELS[value as LicenseModel] ?? String(value);
  }
  if (SNAPSHOT_MONEY_KEYS.includes(key) && typeof value === "number") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 4,
    }).format(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if ((key === "seatTiers" || key === "usageTiers") && Array.isArray(value)) {
    if (value.length === 0) return "No tiers (base scalar rate)";
    return value
      .map((tier) => {
        const item = tier as Record<string, unknown>;
        return `${item.label}: ${item.threshold}+ @ ${item.rate} (FY${item.effectiveFiscalYear} P${item.effectivePeriod})`;
      })
      .join(" · ");
  }
  if (key === "trueUpTiming" && typeof value === "string") {
    return summarizeTrueUp({
      trueUpTiming: value as "none" | "annual" | "contractual",
    });
  }
  return String(value);
}

// Interesting snapshot fields to surface (skip actor, status for retired events since we show it)
const DETAIL_KEYS = [
  "vendor",
  "productWorkload",
  "glAccount",
  "currentModel",
  "futureModel",
  "seats",
  "activeSeats",
  "seatRate",
  "unitRate",
  "fixedMonthlyComponent",
  "minimumMonthlyCommitment",
  "seatTiers",
  "usageTiers",
  "trueUpTiming",
  "trueUpFiscalYear",
  "trueUpFiscalPeriod",
  "trueUpAmount",
  "effectiveFiscalYear",
  "effectivePeriod",
  "sourceReference",
  "status",
];

function SnapshotDetails({
  snapshot,
  eventType,
}: {
  snapshot: Record<string, unknown>;
  eventType: string;
}) {
  const entries = DETAIL_KEYS.filter(
    (k) => k in snapshot && snapshot[k] !== null && snapshot[k] !== undefined
  );

  if (entries.length === 0) return null;

  // For retired events, only show a handful of identity keys
  const keys =
    eventType === "retired"
      ? entries.filter((k) =>
          ["vendor", "productWorkload", "glAccount", "status"].includes(k)
        )
      : entries;

  if (keys.length === 0) return null;

  return (
    <div className="le-audit-snapshot" data-testid="audit-snapshot">
      {keys.map((k) => (
        <div key={k} className="le-audit-snapshot__row">
          <span className="le-audit-snapshot__key">
            {SNAPSHOT_LABEL[k] ?? k}
          </span>
          <span className="le-audit-snapshot__val">
            {displayValue(k, snapshot[k])}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Single event ─────────────────────────────────────────────────────────────

const EVENT_TYPE_LABEL: Record<string, string> = {
  created: "Created",
  updated: "Updated",
  retired: "Retired",
};

const EVENT_TYPE_TONE: Record<string, string> = {
  created: "authoritative",
  updated: "loading",
  retired: "missing",
};

function AuditEventRow({ event }: { event: LicenseEconomicsAuditEvent }) {
  const label = EVENT_TYPE_LABEL[event.eventType] ?? event.eventType;
  const tone = EVENT_TYPE_TONE[event.eventType] ?? "loading";

  return (
    <div
      className="le-audit-event"
      data-testid={`audit-event-${event.auditId}`}
    >
      <div className="le-audit-event__head">
        <span className={`source-status ${tone}`}>{label}</span>
        <span className="le-audit-event__actor">{event.actor}</span>
        <span className="le-audit-event__ts">
          <Clock size={10} />
          {formatTs(event.occurredAt)}
        </span>
      </div>
      <SnapshotDetails
        snapshot={event.snapshot as Record<string, unknown>}
        eventType={event.eventType}
      />
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export function LicenseEconomicsAuditPanel({
  licenseEconomicsId,
}: {
  licenseEconomicsId: string;
}) {
  const { data: events, isLoading, isError, refetch } = useGetLicenseEconomicsAudit(
    licenseEconomicsId,
    {
      query: {
        queryKey: getGetLicenseEconomicsAuditQueryKey(licenseEconomicsId),
        enabled: !!licenseEconomicsId,
      },
    }
  );

  if (isLoading) {
    return (
      <div className="le-loading" data-testid="audit-loading">
        <Loader2 size={14} className="animate-spin" /> Loading audit history…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="sc-form-error" role="alert" data-testid="audit-error">
        <AlertCircle size={13} />
        <div>
          <strong>Could not load audit history.</strong>
          <button
            className="tl-btn"
            onClick={() => refetch()}
            style={{ marginLeft: 10 }}
          >
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const list = events ?? [];

  if (list.length === 0) {
    return (
      <div className="le-audit-empty" data-testid="audit-empty">
        No audit events recorded yet.
      </div>
    );
  }

  return (
    <div className="le-audit-list" data-testid="audit-list">
      {[...list].reverse().map((ev) => (
        <AuditEventRow key={ev.auditId} event={ev} />
      ))}
    </div>
  );
}
